import { FC } from 'react'
import { Feature, FeatureCollection, Polygon } from 'geojson'
import { FillPaint, LinePaint } from 'mapbox-gl'
import {
  existingZoneFillStyleWeb,
  existingZoneLineStyleWeb,
} from '@core/utils/map-styling'
import { Layer, Source } from 'react-map-gl'

/**
 * GeoPolygon component displays polygonal zones on a map using GeoJSON data.
 *
 * Props:
 * - `key`: Unique key for the source, ensuring re-render when necessary.
 * - `id`: Unique identifier for the layer to prevent conflicts in map rendering.
 * - `promoteId`: Field used as a unique identifier for each feature in the data source.
 * - `fillLayerStyle`: Optional custom styling for the polygon fill layer, defaults to `existingZoneFillStyleWeb`.
 * - `lineLayerStyle`: Optional custom styling for the polygon line layer, defaults to `existingZoneLineStyleWeb`.
 * - `featurePolygon`: GeoJSON feature or feature collection containing polygonal data to display.
 */
interface GeoPolygonProps {
  key: string | number
  id: string | number
  promoteId: string
  fillLayerStyle?: FillPaint
  lineLayerStyle?: LinePaint
  featurePolygon: Feature<Polygon> | FeatureCollection<Polygon>
}

export const GeoPolygon: FC<GeoPolygonProps> = ({
  key,
  id,
  promoteId,
  fillLayerStyle = existingZoneFillStyleWeb() as FillPaint,
  lineLayerStyle = existingZoneLineStyleWeb() as LinePaint,
  featurePolygon,
}) => {
  return (
    <Source
      key={`${key}-shape-source`}
      id={`${id}-shape-source`}
      type='geojson'
      promoteId={promoteId}
      data={featurePolygon}
    >
      {/* Fill layer for the polygon, using the specified or default fill style */}
      <Layer id={`${id}-fill-layer`} type={'fill'} paint={fillLayerStyle} />

      {/* Line layer for the polygon's border, using the specified or default line style */}
      <Layer id={`${id}-line-layer`} type={'line'} paint={lineLayerStyle} />
    </Source>
  )
}
